// Wohler Chemical — Entactogens (MDMA-adjacent), ED/Sexual Health, Epitalon Peptide Analogs
// New categories from Wohler_RC_Research_Master_File_v0_1.md and chat_sr analysis

export const entactogenEdEpitalonCompounds = {
  "meai": {
    id: "meai",
    name: "MEAI",
    fullName: "5-Methoxy-2-aminoindane",
    aliases: ["5-MeO-AI", "Pace"],
    categoryId: "entactogens",
    program: "Program E",
    cas: "132741-81-2",
    formula: "C₁₀H₁₃NO",
    molecularWeight: "163.22 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled in the U.S. Structural family of aminoindanes; MDAI (related) is also unscheduled. MDMA-adjacent = heightened scrutiny.",
    benefitScore: 70,
    harmScore: 35,
    evidenceConfidence: "MODERATE",
    status: "Favored — Legal Review Required",
    statusColor: "orange",
    goDecision: "GO — for alcohol use disorder research framing",
    summary: "Entactogen-like compound; pharmacologically and structurally close to MDMA but with a novel aminoindane scaffold. Yale ABN lab has MEAI in Phase I/IIa for alcohol use disorder (Clearmind Medicine), reporting alcohol-like euphoric effects with reduced desire to consume alcohol — making MEAI the most credible MDMA-adjacent compound for therapeutic framing. Best positioning: AUD research tool, not 'legal MDMA.'",
    pros: [
      "Published Phase I/IIa data for AUD (Clearmind Medicine / Yale)",
      "Entactogen-like profile with potential AUD therapeutic signal",
      "Defensible framing as AUD/craving-reduction research tool",
      "Aminoindane scaffold differentiates from MDMA core"
    ],
    cons: [
      "MDMA-adjacent perception — heightened legal/reputational scrutiny",
      "Limited long-term safety data",
      "Recreational overdose fatalities reported in UK/Israel"
    ],
    pharmacology: {
      targets: [
        { receptor: "SERT (serotonin transporter)", activity: "Releaser/inhibitor", value: "Entactogen mechanism" },
        { receptor: "DAT (dopamine transporter)", activity: "Weaker vs MDMA", value: "Reduced stimulant burden" },
        { receptor: "NET", activity: "Releaser", value: "Contributes to cardiovascular effects" }
      ],
      duration: "2–4 h",
      mechanism: "Serotonin-dominant monoamine releaser — entactogen effects with reduced dopaminergic stimulation vs MDMA"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Multi-step from 5-methoxyindanone via reduction and amination.",
      steps: [
        { step: "1", title: "5-Methoxyindanone reduction", description: "NaBH₄ reduction of 5-methoxyindanone to alcohol.", solvent: "Methanol", keyReagents: "NaBH₄" },
        { step: "2", title: "Dehydration / alkene formation", description: "Eliminate to indene.", solvent: "Toluene (substitute: Heptane)", keyReagents: "Acid catalyst" },
        { step: "3", title: "Aminomethylation", description: "Hofmann-Löffler-type or hydroamination.", solvent: "THF", keyReagents: "Various amination protocols" },
        { step: "4", title: "HCl salt + crystallization", description: "HCl/EtOAc; recrystallize ethanol/heptane.", solvent: "EtOAc/EtOH/Heptane", keyReagents: "HCl" }
      ],
      yield: "20–35 % over multi-step route",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Moderate — proceed only after legal memo and institutional partner in place",
      solventsUsed: ["Methanol", "THF", "Ethyl Acetate", "Ethanol", "Heptane"],
      equipment: ["Multi-step reactor", "Hydrogenation or amination rig", "Crystallizer"],
      batchPlan: { targetBatch: "100 g first", startingMaterial: "0.5 kg 5-methoxyindanone", keyVolumes: ["MeOH: 10 L", "THF: 15 L"] },
      hazards: ["NaBH₄ — H₂ evolution on water contact; vented addition", "MDMA-adjacent — entrapment-grade industrial hygiene"],
      vendorNotes: ["5-Methoxyindanone: Sigma, Combi-Blocks"],
      estCostPerKg: "$35,000 – $55,000"
    },
    safety: {
      acuteToxicity: "Recreational fatalities reported — serotonin syndrome + hyperthermia concerns",
      chronicConcerns: ["Serotonin neurotoxicity (class)", "Cardiovascular load"],
      contraindications: ["SSRIs, MAOIs", "Cardiac disease", "Pregnancy"],
      acuteSideEffects: ["Entactogenic effects", "Empathy/prosocial", "Hyperthermia risk"],
      ehsClassification: "GHS Cat. 3 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Yale ABN lab / Clearmind Medicine. MEAI Phase I/IIa for alcohol use disorder. 2024." },
      { citation: "Brandt SD et al. The design and preparation of an entactogen, MEAI. Drug Test Anal. 2017." }
    ],
    patentInfo: "Clearmind Medicine holds key patents for AUD indication. Wohler can produce research-scale.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 20, evidenceQuality: 15, unmetMedicalNeed: 19, novelty: 10, universityInterest: 6 },
      harmBreakdown: { acuteToxicity: 8, chronicToxicity: 10, dependence: 7, schedulingRisk: 10 },
      netValue: 35,
      confidenceRationale: "MODERATE — clinical signal emerging; substantial regulatory/reputational overhead."
    },
    priorityTier: "Tier 2",
    nextSteps: ["Legal memo on entactogen analog risk", "Partner with Clearmind or Yale", "Focus on AUD framing"]
  },

  "sdma": {
    id: "sdma",
    name: "SDMA",
    fullName: "SDMA (1,3-Benzoxathiole MDMA analog)",
    aliases: [],
    categoryId: "entactogens",
    program: "Program E",
    formula: "~C₁₀H₁₃NOS",
    molecularWeight: "~195 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Very close MDMA analog — highest analog-act enforcement concern. Counsel-first posture mandatory.",
    benefitScore: 65,
    harmScore: 55,
    evidenceConfidence: "LOW",
    status: "Hold — Legal-First",
    statusColor: "red",
    goDecision: "HOLD — literature watch only until counsel narrows",
    summary: "Next-generation MDMA analog where the 1,3-benzodioxole group of MDMA is replaced by a 1,3-benzoxathiole. 2025 research characterizes SDMA-family compounds for SAR separation of therapeutic entactogenic effects from toxicity and abuse liability. Too close to MDMA for casual Wohler engagement.",
    pros: ["Genuine SAR innovation for separating therapy from toxicity", "Published 2025 research"],
    cons: ["Extremely close to MDMA — analog-act high risk", "Reputational risk for Wohler", "No clinical development pathway"],
    pharmacology: {
      targets: [{ receptor: "SERT/DAT/NET", activity: "Releaser (MDMA-like)", value: "Characterization underway" }],
      duration: "Estimated MDMA-range",
      mechanism: "Monoamine releaser"
    },
    labSynthesis: {
      title: "Not Recommended",
      overview: "Synthesis is feasible but Wohler should not prioritize this compound over safer alternatives.",
      steps: [{ step: "1", title: "HOLD", description: "Do not commit synthesis capacity until legal memo complete.", solvent: "N/A", keyReagents: "N/A" }],
      yield: "TBD",
      purityTarget: "N/A"
    },
    kgScaleSynthesis: {
      title: "Not Recommended",
      feasibility: "Not pursued",
      solventsUsed: [],
      equipment: [],
      batchPlan: {},
      hazards: ["Analog-act enforcement risk"],
      vendorNotes: ["N/A"],
      estCostPerKg: "N/A"
    },
    safety: {
      acuteToxicity: "Assume MDMA-range",
      chronicConcerns: ["Serotonin neurotoxicity", "Cardiovascular"],
      contraindications: ["MDMA-class"],
      acuteSideEffects: ["Entactogenic"],
      ehsClassification: "GHS Cat. 3 oral (suspected)"
    },
    publishedResearch: [
      { citation: "Next-generation MDMA analogs — SDMA SAR. 2025." }
    ],
    patentInfo: "Academic — under characterization.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 16, evidenceQuality: 10, unmetMedicalNeed: 18, novelty: 14, universityInterest: 7 },
      harmBreakdown: { acuteToxicity: 10, chronicToxicity: 13, dependence: 10, schedulingRisk: 22 },
      netValue: 10,
      confidenceRationale: "LOW — too new to evaluate fully"
    },
    priorityTier: "KILL — On-hold pending legal",
    nextSteps: ["Literature watch only"]
  },

  "r-mddma": {
    id: "r-mddma",
    name: "R-MDDMA",
    fullName: "R-MDDMA (chiral MDMA analog)",
    aliases: [],
    categoryId: "entactogens",
    program: "Program E",
    formula: "~MDMA scaffold variant",
    molecularWeight: "~215 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled as of 2026. Extremely new — emerging literature.",
    benefitScore: 68,
    harmScore: 50,
    evidenceConfidence: "LOW",
    status: "Hold — Too New",
    statusColor: "red",
    goDecision: "HOLD — literature watch; too early",
    summary: "Very recent literature explores R-MDDMA as a potentially safer MDMA analog with chiral separation of therapeutic from adverse properties. Too new for production decisions. Listed here for research tracking.",
    pros: ["Potentially safer MDMA analog"],
    cons: ["Too new to evaluate", "MDMA analog scheduling risk"],
    pharmacology: {
      targets: [{ receptor: "SERT (predominantly)", activity: "Releaser", value: "Characterization underway" }],
      duration: "Unknown",
      mechanism: "Entactogen-class"
    },
    labSynthesis: { title: "Not Recommended Yet", overview: "Literature-watch only.", steps: [{ step: "1", title: "Watch", description: "Hold production.", solvent: "N/A", keyReagents: "N/A" }], yield: "TBD", purityTarget: "N/A" },
    kgScaleSynthesis: { title: "Not Evaluated", feasibility: "TBD", solventsUsed: [], equipment: [], batchPlan: {}, hazards: [], vendorNotes: [], estCostPerKg: "N/A" },
    safety: {
      acuteToxicity: "Unknown",
      chronicConcerns: ["Assume MDMA-class serotonergic concerns"],
      contraindications: ["MDMA-class"],
      acuteSideEffects: ["Unknown"],
      ehsClassification: "TBD"
    },
    publishedResearch: [{ citation: "R-MDDMA emerging literature. 2025." }],
    patentInfo: "Academic.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 17, evidenceQuality: 8, unmetMedicalNeed: 18, novelty: 17, universityInterest: 8 },
      harmBreakdown: { acuteToxicity: 10, chronicToxicity: 13, dependence: 10, schedulingRisk: 17 },
      netValue: 18,
      confidenceRationale: "LOW"
    },
    priorityTier: "Watch — Do not produce",
    nextSteps: ["Literature watch"]
  },

  "pt-141": {
    id: "pt-141",
    name: "PT-141 Analogs",
    fullName: "Bremelanotide-like MC4R agonists",
    aliases: ["Bremelanotide analogs"],
    categoryId: "ed-sexual",
    program: "Program G",
    formula: "Peptide (11-mer cyclic)",
    molecularWeight: "~1025 Da (bremelanotide)",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Parent compound bremelanotide (Vyleesi) is FDA-approved (HSDD). Novel analogs are research compounds.",
    benefitScore: 80,
    harmScore: 18,
    evidenceConfidence: "HIGH",
    status: "Strongly Favored",
    statusColor: "green",
    goDecision: "GO — strong ED/sexual health research candidate",
    summary: "Melanocortin-4 receptor (MC4R) agonist acting centrally (not via PDE5/vasodilation). Works in populations where PDE5 inhibitors fail (nerve damage, psychological ED, female sexual dysfunction). FDA-approved parent compound has strong safety profile. Novel peptide analogs represent legitimate research space.",
    pros: [
      "Centrally-acting mechanism — works where PDE5 inhibitors fail",
      "Female and male sexual dysfunction applicability",
      "Strong parent safety profile (bremelanotide Vyleesi approved)",
      "Active research interest in peptide analogs",
      "Post-prostatectomy ED research angle directly relevant to Wohler founder story"
    ],
    cons: [
      "Peptide synthesis — specialized SPPS equipment required",
      "Nausea is a known class effect",
      "FDA-approved parent creates Rx-drug adjacency (must maintain research positioning)"
    ],
    pharmacology: {
      targets: [
        { receptor: "MC4R", activity: "Agonist", value: "Central mechanism" },
        { receptor: "MC3R", activity: "Partial agonist", value: "Contributes to effects" }
      ],
      duration: "4–8 h",
      mechanism: "Central MC4R agonism → hypothalamic arousal pathway → sexual arousal/response in both sexes"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Solid-phase peptide synthesis (SPPS) using Fmoc chemistry. Cyclic lactam formation via on-resin or solution cyclization.",
      steps: [
        { step: "1", title: "SPPS linear peptide", description: "Fmoc-protected amino acids on Rink amide resin. Standard DIC/HOBt couplings.", solvent: "DMF (peptide-grade; not a Wohler standard solvent — requires process evaluation)", keyReagents: "Fmoc amino acids, DIC, HOBt" },
        { step: "2", title: "Side-chain cyclization", description: "Selective Alloc or Dde removal, on-resin lactamization.", solvent: "DMF", keyReagents: "HATU, DIPEA" },
        { step: "3", title: "Cleavage + deprotection", description: "TFA cleavage cocktail (95:2.5:2.5 TFA/H₂O/TIS).", solvent: "TFA/H₂O/TIS", keyReagents: "TFA" },
        { step: "4", title: "Prep HPLC purification", description: "Reversed-phase C18, acetonitrile/water/TFA gradient.", solvent: "Acetonitrile/water", keyReagents: "TFA modifier" },
        { step: "5", title: "Lyophilization", description: "Freeze-dry purified peptide.", solvent: "Water", keyReagents: "N/A" }
      ],
      yield: "15–30 % (typical for cyclic peptide)",
      purityTarget: "≥ 98 % HPLC; confirmed by ESI-MS, amino acid analysis"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Low — peptide synthesis is not Wohler's current core competency. 100-g is realistic first target; kg requires significant capital for industrial SPPS equipment.",
      solventsUsed: ["DMF (peptide process solvent — NOT Wohler standard; requires evaluation of DMAc or NMP alternatives)", "Acetonitrile", "Water", "Methanol", "Ethanol"],
      equipment: [
        "Industrial SPPS synthesizer (CEM Liberty PRO or equivalent, 100-g scale)",
        "Preparative HPLC skid with C18 column",
        "Lyophilizer"
      ],
      batchPlan: { targetBatch: "100 g PT-141 analog", startingMaterial: "Fmoc-amino acids + resin", keyVolumes: ["DMF: 300 L (if kg scale)", "ACN for HPLC: 500 L"] },
      hazards: ["TFA corrosive", "DMF hepatotoxic — containment required", "Peptide dust — sensitizer"],
      vendorNotes: ["Fmoc-amino acids: CEM, Combi-Blocks, Iris Biotech", "Rink amide resin: CEM, PolyPeptide Labs"],
      estCostPerKg: "$180,000 – $350,000 at 100-g (peptide economics)"
    },
    safety: {
      acuteToxicity: "Bremelanotide Phase III data: well-tolerated; nausea most common AE",
      chronicConcerns: ["Hyperpigmentation (rare)", "Blood pressure elevation"],
      contraindications: ["Uncontrolled hypertension", "Cardiovascular disease"],
      acuteSideEffects: ["Nausea (40 % Phase III)", "Hyperpigmentation", "Transient BP elevation"],
      ehsClassification: "GHS Cat. 4 (peptide — sensitizer class)"
    },
    publishedResearch: [
      { citation: "Kingsberg SA et al. Bremelanotide for the Treatment of Hypoactive Sexual Desire Disorder. Obstet Gynecol. 2019." },
      { citation: "Palatin Technologies. PT-141 / Bremelanotide preclinical + clinical program. 2000-2019." }
    ],
    patentInfo: "Palatin Technologies holds bremelanotide IP. Novel MC4R analogs represent patentable space.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 19, unmetMedicalNeed: 20, novelty: 12, universityInterest: 7 },
      harmBreakdown: { acuteToxicity: 4, chronicToxicity: 6, dependence: 2, schedulingRisk: 6 },
      netValue: 62,
      confidenceRationale: "HIGH — strong clinical precedent; clear research pathway"
    },
    priorityTier: "Tier 2 — Program G lead",
    nextSteps: ["Evaluate outsourced SPPS partnership vs internal capability build", "Identify post-prostatectomy ED research partner", "IP landscape analysis"]
  },

  "icariin": {
    id: "icariin",
    name: "Icariin / Icariside II",
    fullName: "Icariin and Icariside II (flavonoid PDE5 modulators)",
    aliases: ["Epimedium flavonoids"],
    categoryId: "ed-sexual",
    program: "Program G",
    cas: "489-32-7 (icariin)",
    formula: "C₃₃H₄₀O₁₅ (icariin)",
    molecularWeight: "676.67 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Used in traditional Chinese medicine; dietary supplement space has FDA attention on claims.",
    benefitScore: 70,
    harmScore: 12,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — low-controversy ED research candidate",
    summary: "Flavonoid PDE5 inhibitor from Epimedium (horny goat weed). Icariin itself is ~1000× less potent than sildenafil, but its metabolite icariside II is substantially more potent. Multi-mechanism: PDE5 inhibition + neuroprotection + testosterone-potentiating. Very low scheduling risk, clean research narrative.",
    pros: [
      "Low scheduling + regulatory risk",
      "Multi-mechanism profile (PDE5 + neuroprotection + testosterone)",
      "Strong traditional Chinese medicine safety record",
      "Natural product = clean research positioning",
      "Diabetic ED research angle"
    ],
    cons: ["Weaker primary PDE5 inhibition than synthetic inhibitors", "Complex natural product — standardization challenge"],
    pharmacology: {
      targets: [
        { receptor: "PDE5", activity: "Inhibitor (modest)", value: "IC50 ~1 μM (icariside II stronger)" },
        { receptor: "Androgen receptor (indirect)", activity: "Testosterone-potentiating", value: "Adjunctive mechanism" },
        { receptor: "Neuroprotection pathways", activity: "Activator", value: "Additional benefit" }
      ],
      duration: "6–8 h",
      mechanism: "Multi-target: PDE5 + endothelial NO + testosterone + neuroprotection"
    },
    labSynthesis: {
      title: "Laboratory-Scale Isolation",
      overview: "Isolation from Epimedium biomass + enzymatic/chemical hydrolysis to icariside II.",
      steps: [
        { step: "1", title: "Epimedium extraction", description: "Dried Epimedium leaf extracted with 70 % ethanol. Concentrate; partition with EtOAc.", solvent: "Ethanol / EtOAc", keyReagents: "70 % EtOH" },
        { step: "2", title: "Silica chromatography", description: "CHCl₃/MeOH gradient (substitute EtOAc/MeOH for Wohler). Icariin elutes at high polarity.", solvent: "EtOAc/MeOH", keyReagents: "Silica" },
        { step: "3", title: "Enzymatic/acid hydrolysis to icariside II", description: "β-Glucosidase or mild HCl to cleave rhamnose-glucose unit.", solvent: "Ethanol/water", keyReagents: "β-Glucosidase or HCl" },
        { step: "4", title: "Recrystallization", description: "Ethanol/water.", solvent: "Ethanol/water", keyReagents: "Ethanol" }
      ],
      yield: "~1–3 g icariin per kg dry leaf; ~30–50 % conversion to icariside II",
      purityTarget: "≥ 98 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "High — isolation-based, Wohler has biomass processing expertise",
      solventsUsed: ["Ethanol", "Ethyl Acetate", "Methanol", "Water", "Heptane"],
      equipment: ["Large extraction tank", "SMB chromatography", "Crystallizer"],
      batchPlan: {
        targetBatch: "1 kg icariin + 100 g icariside II",
        startingMaterial: "~400 kg dried Epimedium leaf",
        keyVolumes: ["70% EtOH extraction: 2000 L", "EtOAc: 500 L", "MeOH chromatography: 150 L"]
      },
      hazards: ["Standard biomass handling", "Biochemical hazards from enzymatic step"],
      vendorNotes: ["Epimedium leaf: Chinese herb suppliers", "β-Glucosidase: Sigma, Novozymes"],
      estCostPerKg: "$8,000 – $15,000 (icariin); $40,000–$70,000 (icariside II)"
    },
    safety: {
      acuteToxicity: "LD50 rat > 2000 mg/kg oral — very favorable",
      chronicConcerns: ["Estrogenic activity reported — caution with hormone-sensitive conditions"],
      contraindications: ["Hormone-sensitive cancers", "Pregnancy"],
      acuteSideEffects: ["Generally mild; flushing at high dose"],
      ehsClassification: "GHS Cat. 5 oral (low toxicity)"
    },
    publishedResearch: [
      { citation: "Dell'Agli M et al. Potent inhibition of human phosphodiesterase-5 by icariin derivatives. J Nat Prod. 2008;71(9):1513-7." },
      { citation: "Multiple TCM literature reviews on Epimedium." }
    ],
    patentInfo: "Natural products — composition not patentable. Formulation, standardization, and method-of-use patents available.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 18, evidenceQuality: 16, unmetMedicalNeed: 18, novelty: 8, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 2, chronicToxicity: 4, dependence: 1, schedulingRisk: 5 },
      netValue: 58,
      confidenceRationale: "MODERATE — good safety, moderate efficacy"
    },
    priorityTier: "Tier 2",
    nextSteps: ["Standardized icariin USP-grade production", "University ED research partner", "Icariside II semi-synthesis optimization"]
  },

  "epitalon": {
    id: "epitalon",
    name: "Epitalon Analogs",
    fullName: "Epitalon (Ala-Glu-Asp-Gly) + half-life-extension analogs",
    aliases: ["Epithalon"],
    categoryId: "peptides",
    program: "Program I",
    cas: "307297-39-8 (epitalon)",
    formula: "C₁₄H₂₂N₄O₉ (epitalon)",
    molecularWeight: "390.35 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Not an approved drug in the U.S. FDA/peptide/drug-claim concerns if marketed for human use.",
    benefitScore: 68,
    harmScore: 12,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — peptide PK-extension research program",
    summary: "Epitalon is a tetrapeptide (Ala-Glu-Asp-Gly) developed by Vladimir Khavinson for circadian, telomerase, pineal gland, and longevity research. Rapidly degraded in plasma — half-life extension analogs (D-amino acid substitutions, PEGylation, fatty acid conjugation) are the Wohler program focus. Good patent/IP research program.",
    pros: [
      "Very low toxicity profile",
      "Open patent space for PK-extended analogs",
      "Circadian + longevity research market interest",
      "Simple parent peptide; feasible SPPS",
      "Russian clinical literature 30+ years"
    ],
    cons: ["Rapid parent peptide degradation", "Efficacy vs placebo claims need rigorous study", "FDA scrutiny on longevity claims"],
    pharmacology: {
      targets: [
        { receptor: "Telomerase (indirect)", activity: "Inducer", value: "Hypothesized mechanism" },
        { receptor: "Pineal / circadian regulation", activity: "Modulator", value: "Russian literature" }
      ],
      duration: "Native peptide very short; analogs targeted for 6–12 h",
      halfLife: "Parent <5 min; D-amino acid analogs 30–60 min; PEG analogs 6–24 h",
      mechanism: "Proposed: telomerase activation, melatonin/circadian modulation, anti-inflammatory — mechanism remains incompletely validated"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "SPPS of Ala-Glu-Asp-Gly parent, plus modified analogs: D-Ala substitution, N-terminal acetylation, PEGylation, palmitoylation for half-life extension.",
      steps: [
        { step: "1", title: "SPPS linear", description: "Fmoc-Gly-Wang resin; add Fmoc-Asp(OtBu), Fmoc-Glu(OtBu), Fmoc-Ala (or D-Ala).", solvent: "DMF (peptide solvent)", keyReagents: "Fmoc amino acids, DIC/HOBt" },
        { step: "2", title: "Modifications (analog-specific)", description: "Optional: N-terminal PEGylation with PEG-NHS, palmitoylation with palmitic acid + HATU.", solvent: "DMF", keyReagents: "PEG-NHS or palmitic acid" },
        { step: "3", title: "Cleavage + deprotection", description: "TFA/H₂O/TIS cocktail.", solvent: "TFA/H₂O/TIS", keyReagents: "TFA" },
        { step: "4", title: "Prep HPLC purification", description: "C18 reversed-phase, acetonitrile/water/TFA.", solvent: "Acetonitrile/water", keyReagents: "TFA" },
        { step: "5", title: "Lyophilization", description: "Freeze-dry.", solvent: "Water", keyReagents: "N/A" }
      ],
      yield: "50–70 % for parent; 30–50 % for modified analogs",
      purityTarget: "≥ 98 % HPLC, confirmed by ESI-MS"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Moderate for parent; partnership with CMO likely most economical",
      solventsUsed: ["DMF (peptide process solvent — non-Wohler-standard, evaluate NMP alt)", "Acetonitrile", "Water", "Ethanol"],
      equipment: ["Industrial SPPS synthesizer", "Preparative HPLC", "Lyophilizer"],
      batchPlan: { targetBatch: "100 g parent epitalon", startingMaterial: "Fmoc amino acids", keyVolumes: ["DMF: 200 L", "ACN: 300 L"] },
      hazards: ["TFA, DMF — standard peptide hazards"],
      vendorNotes: ["Iris Biotech, CEM, PolyPeptide for Fmoc amino acids and resins"],
      estCostPerKg: "$80,000 – $150,000 at 100-g scale"
    },
    safety: {
      acuteToxicity: "Very favorable — non-toxic in Russian clinical literature",
      chronicConcerns: ["Long-term telomerase modulation unknown", "Pineal gland effects"],
      contraindications: ["Pregnancy"],
      acuteSideEffects: ["Minimal"],
      ehsClassification: "GHS Cat. 5 oral"
    },
    publishedResearch: [
      { citation: "Khavinson VK et al. Peptide bioregulation of aging. Bull Exp Biol Med. 2003+." },
      { citation: "Anisimov VN et al. Effect of Epitalon on biomarkers of aging, life span and spontaneous tumor incidence in female Swiss mice. Biogerontology. 2003." }
    ],
    patentInfo: "Parent composition expired. PK-extended analogs represent open patent space — strong Wohler opportunity.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 16, evidenceQuality: 14, unmetMedicalNeed: 18, novelty: 14, universityInterest: 6 },
      harmBreakdown: { acuteToxicity: 2, chronicToxicity: 4, dependence: 1, schedulingRisk: 5 },
      netValue: 56,
      confidenceRationale: "MODERATE — safety excellent; efficacy claims need rigorous validation"
    },
    priorityTier: "Tier 2 — IP opportunity",
    nextSteps: ["File provisional patents on 2–3 PK-extended analogs", "Establish CMO partnership for SPPS", "Longevity research partner identification"]
  },

  "dor-agonists": {
    id: "dor-agonists",
    name: "Delta Opioid Receptor (DOR) Agonists",
    fullName: "Selective DOR Agonists for Chronic Pain (class)",
    aliases: ["DOR agonists"],
    categoryId: "pain",
    program: "Program D",
    formula: "Class — various",
    molecularWeight: "Varies",
    schedulingStatus: "unscheduled",
    schedulingNote: "Class — specific compounds vary. Most research DOR agonists are unscheduled. Seizure risk is historical concern for class.",
    benefitScore: 75,
    harmScore: 28,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — pain/mood research without MOR or KOR baggage",
    summary: "Delta opioid receptors are overlooked but promising for chronic pain, allodynia, hyperalgesia, and mood disorders. A 2025 review describes DOR as lacking the euphoric/addictive profile of mu and the dysphoria of kappa. Relevant to neuropathic pain and mood without mu-opioid risk.",
    pros: ["Non-mu, non-kappa opioid pain target", "Mood-benefit research angle", "Lower abuse liability than mu opioids", "No respiratory depression (class)"],
    cons: ["Seizure risk (historical concern; newer compounds safer)", "No single lead compound identified for Wohler production"],
    pharmacology: {
      targets: [
        { receptor: "δ-opioid", activity: "Agonist (selective)", value: "Compound-specific" }
      ],
      duration: "Varies",
      mechanism: "DOR agonism → analgesia with mood benefit; distinct from mu/kappa mechanisms"
    },
    labSynthesis: { title: "Compound-specific", overview: "Wohler to select 2–3 research-tool DOR agonists.", steps: [{ step: "1", title: "Compound selection", description: "Literature selection.", solvent: "N/A", keyReagents: "N/A" }], yield: "TBD", purityTarget: "≥ 99 %" },
    kgScaleSynthesis: {
      title: "Compound-Specific",
      feasibility: "TBD",
      solventsUsed: ["Wohler standard solvents"],
      equipment: ["Standard"],
      batchPlan: { targetBatch: "100 g per lead", startingMaterial: "Varies", keyVolumes: ["TBD"] },
      hazards: ["Compound-specific"],
      vendorNotes: ["Sigma"],
      estCostPerKg: "TBD"
    },
    safety: {
      acuteToxicity: "Class generally favorable",
      chronicConcerns: ["Seizure risk (historical; compound-specific)"],
      contraindications: ["Seizure disorder"],
      acuteSideEffects: ["Analgesia, mild mood elevation"],
      ehsClassification: "Compound-specific"
    },
    publishedResearch: [
      { citation: "Delta opioid receptor research — chronic pain and mood. 2025 review." }
    ],
    patentInfo: "Active industry + academic patents.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 21, evidenceQuality: 14, unmetMedicalNeed: 20, novelty: 13, universityInterest: 7 },
      harmBreakdown: { acuteToxicity: 6, chronicToxicity: 10, dependence: 6, schedulingRisk: 6 },
      netValue: 47,
      confidenceRationale: "MODERATE — class rationale strong; compound selection pending"
    },
    priorityTier: "Tier 2 — Class selection required",
    nextSteps: ["Lead compound selection", "Seizure-liability prescreening plan"]
  },

  "nop-mor-mixed": {
    id: "nop-mor-mixed",
    name: "Mixed NOP/MOR Ligands",
    fullName: "Mixed Nociceptin/Mu-Opioid Receptor Agonists (class)",
    aliases: ["NOP/MOR bitopic"],
    categoryId: "pain",
    program: "Program D",
    formula: "Class — various",
    molecularWeight: "Varies",
    schedulingStatus: "unscheduled",
    schedulingNote: "Class-dependent. Cebranopadol (NOP/MOR mixed) is in late-stage clinical development; unscheduled currently.",
    benefitScore: 82,
    harmScore: 26,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — potentially the best next-generation pain lane",
    summary: "Mixed nociceptin (NOP) + mu-opioid receptor agonists preserve potent analgesia while reducing abuse liability, tolerance, and respiratory depression. Cebranopadol is the most-advanced clinical example. This mechanism — not 'make another mu agonist' — may be the best next-generation pain research direction.",
    pros: [
      "Strong analgesia with reduced abuse + respiratory depression",
      "Not simply 'another mu agonist' — mechanistically differentiated",
      "Late-stage clinical validation (cebranopadol)",
      "University/CRO research interest high"
    ],
    cons: ["Complex synthesis for most advanced compounds", "IP is tightly held by industry"],
    pharmacology: {
      targets: [
        { receptor: "NOP (ORL1)", activity: "Agonist", value: "Blunts abuse/reward" },
        { receptor: "μ-opioid", activity: "Partial agonist", value: "Analgesia" }
      ],
      duration: "Varies",
      mechanism: "NOP agonism counterbalances MOR reward signaling → analgesia without abuse"
    },
    labSynthesis: {
      title: "Compound-Specific",
      overview: "Wohler to select 1–2 research-tool NOP/MOR mixed ligands. Candidate: SR-16835 or similar published research tool.",
      steps: [{ step: "1", title: "Compound selection", description: "Literature + IP review.", solvent: "N/A", keyReagents: "N/A" }],
      yield: "TBD",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Compound-Specific",
      feasibility: "Moderate",
      solventsUsed: ["Wohler standard"],
      equipment: ["Standard multi-step"],
      batchPlan: { targetBatch: "10–100 g initial", startingMaterial: "Varies", keyVolumes: ["TBD"] },
      hazards: ["Opioid-class industrial hygiene"],
      vendorNotes: ["Sigma, Combi-Blocks"],
      estCostPerKg: "TBD"
    },
    safety: {
      acuteToxicity: "Cebranopadol Phase II/III data favorable",
      chronicConcerns: ["Reduced but non-zero respiratory depression"],
      contraindications: ["Opioid-class"],
      acuteSideEffects: ["Analgesia, minor nausea"],
      ehsClassification: "Opioid industrial hygiene"
    },
    publishedResearch: [
      { citation: "Schunk S et al. Discovery of a Potent Analgesic NOP and Opioid Receptor Agonist: Cebranopadol. ACS Med Chem Lett. 2014." },
      { citation: "Mixed NOP/opioid agonists review. 2024." }
    ],
    patentInfo: "Grünenthal holds cebranopadol IP. Research-tool compounds composition-open.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 23, evidenceQuality: 17, unmetMedicalNeed: 22, novelty: 15, universityInterest: 5 },
      harmBreakdown: { acuteToxicity: 5, chronicToxicity: 8, dependence: 6, schedulingRisk: 7 },
      netValue: 56,
      confidenceRationale: "MODERATE — strong class rationale with clinical validation"
    },
    priorityTier: "Tier 2",
    nextSteps: ["Select 1–2 research tools", "IP landscape review", "Partner with pain research lab"]
  },

  "peripheral-opioids": {
    id: "peripheral-opioids",
    name: "Peripherally-Restricted Opioid Agonists",
    fullName: "Peripherally-restricted opioid receptor agonists (class)",
    aliases: ["Peripheral opioids"],
    categoryId: "pain",
    program: "Program D",
    formula: "Class — various",
    molecularWeight: "Varies",
    schedulingStatus: "unscheduled",
    schedulingNote: "Class — loperamide is a classic approved example (OTC). Peripheral restriction via physicochemical properties or efflux substrate design.",
    benefitScore: 75,
    harmScore: 18,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — clean pain research without CNS abuse liability",
    summary: "Peripheral opioid receptor agonists act at opioid receptors outside the CNS, producing analgesia (especially visceral and inflammatory pain) without central euphoria or respiratory depression. Represents a very good research direction if the goal is to help pain without creating fentanyl-like CNS risk.",
    pros: [
      "No central abuse or respiratory depression",
      "Clean 'fentanyl-harm-reduction' research narrative",
      "Multiple lead compounds in development",
      "Suitable for visceral + inflammatory pain research"
    ],
    cons: ["Limited utility for severe CNS-mediated pain", "P-gp substrate design can create PK complexity"],
    pharmacology: {
      targets: [{ receptor: "μ/δ/κ peripheral", activity: "Agonist (peripherally restricted)", value: "Compound-specific" }],
      duration: "Varies",
      mechanism: "Peripheral opioid agonism without CNS penetration — dose-limited by peripheral constipation rather than CNS effects"
    },
    labSynthesis: { title: "Compound-Specific", overview: "Wohler to select research-tool compounds.", steps: [{ step: "1", title: "Selection", description: "Literature + SAR review for P-gp substrate design.", solvent: "N/A", keyReagents: "N/A" }], yield: "TBD", purityTarget: "≥ 99 %" },
    kgScaleSynthesis: {
      title: "Compound-Specific",
      feasibility: "Moderate",
      solventsUsed: ["Wohler standard"],
      equipment: ["Standard"],
      batchPlan: { targetBatch: "100 g per lead", startingMaterial: "Varies", keyVolumes: ["TBD"] },
      hazards: ["Compound-specific"],
      vendorNotes: ["Sigma"],
      estCostPerKg: "TBD"
    },
    safety: {
      acuteToxicity: "Class generally favorable (loperamide-like)",
      chronicConcerns: ["Cardiac arrhythmia at high doses (loperamide precedent)"],
      contraindications: ["Abuse of loperamide has occurred at very high doses — QT prolongation"],
      acuteSideEffects: ["Peripheral analgesia", "Constipation (target peripheral ME)"],
      ehsClassification: "Compound-specific"
    },
    publishedResearch: [
      { citation: "Peripheral opioid receptor agonists for visceral pain. Various 2020-2024 reviews." }
    ],
    patentInfo: "Active patent space.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 22, evidenceQuality: 14, unmetMedicalNeed: 20, novelty: 13, universityInterest: 6 },
      harmBreakdown: { acuteToxicity: 3, chronicToxicity: 6, dependence: 4, schedulingRisk: 5 },
      netValue: 57,
      confidenceRationale: "MODERATE"
    },
    priorityTier: "Tier 2",
    nextSteps: ["Lead compound selection", "P-gp substrate validation plan"]
  },

  "modafinil-anti-inflammatory-11a-k": {
    id: "modafinil-anti-inflammatory-11a-k",
    name: "Modafinil Anti-Inflammatory Derivatives (11a-k, 12a-k)",
    fullName: "Modafinil-derived benzhydrylsulfinyl/sulfonyl anti-inflammatory compounds (Jung 2012, series 11a-k and 12a-k)",
    aliases: [],
    categoryId: "cognitive",
    program: "Program C",
    formula: "Series — various modafinil analogs",
    molecularWeight: "Varies (~280–350 g/mol per analog)",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Modafinil (parent) is Schedule IV; these anti-inflammatory derivatives are non-stimulant research tools.",
    benefitScore: 72,
    harmScore: 18,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — anti-inflammatory framing reduces abuse concern",
    summary: "11a-k and 12a-k derivatives of modafinil from Jung et al. 2012 (Molecules). Several derivatives suppress nitrite generation and iNOS/COX-2 expression in LPS-stimulated BV2 microglia — supporting a wakefulness + neuroinflammation research framing rather than stimulant/nootropic. Positioned as anti-inflammatory research tools reduces analog scheduling concern vs wakefulness-focused F-modafinil.",
    pros: [
      "Non-stimulant positioning — reduced scheduling scrutiny vs F-modafinil",
      "Direct anti-neuroinflammatory data in BV2 microglia",
      "Relevant to Parkinson's/Alzheimer's-adjacent research",
      "Series of 20+ analogs → SAR research package",
      "Published synthesis from Jung 2012"
    ],
    cons: ["Selection of which 11/12 analog to produce first requires review", "Some derivatives may still have wakefulness activity"],
    pharmacology: {
      targets: [
        { receptor: "iNOS (microglia)", activity: "Downregulator", value: "BV2 LPS model" },
        { receptor: "COX-2 (microglia)", activity: "Downregulator", value: "BV2 LPS model" },
        { receptor: "NF-κB pathway (hypothesized)", activity: "Modulator", value: "Downstream of NO reduction" }
      ],
      duration: "Unknown — analog-specific",
      mechanism: "Microglial anti-inflammatory via NO/iNOS/COX-2 suppression; distinct from wakefulness mechanism"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis (Jung 2012 route)",
      overview: "From diphenylmethyl thioglycolic acid derivatives: S-alkylation → H₂O₂ oxidation to sulfoxide/sulfone → amide coupling (Lawesson's reagent for thioamides).",
      steps: [
        { step: "1", title: "Thioacetic acid alkylation", description: "Benzhydryl thioglycolic acid + base (K₂CO₃) in ethanol; alkyl halide or aryl halide coupling.", solvent: "Ethanol", keyReagents: "Thioglycolic acid, K₂CO₃" },
        { step: "2", title: "Oxidation", description: "H₂O₂ in MeOH/AcOH to sulfoxide (controlled); further H₂O₂/AcOH to sulfone if needed.", solvent: "Methanol / acetic acid", keyReagents: "H₂O₂" },
        { step: "3", title: "Amide coupling or thioamide formation", description: "Lawesson's reagent for thioamides (some 11/12 series); amide via acid chloride + amine.", solvent: "THF or Toluene (substitute: EtOAc with elevated temperature)", keyReagents: "Lawesson's reagent or acid chloride route" },
        { step: "4", title: "Purification + crystallization", description: "Flash chromatography; recrystallize from EtOAc/Heptane or Ethanol.", solvent: "EtOAc/Heptane or Ethanol", keyReagents: "Silica" }
      ],
      yield: "25–45 % per analog (from Jung 2012)",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan (per lead analog)",
      feasibility: "Moderate — each analog scale-up is single-lead; total series is 100-g scale",
      solventsUsed: ["Ethyl Acetate", "Ethanol", "Methanol", "THF", "Acetic Acid", "Heptane"],
      equipment: ["Multi-step reactor", "H₂O₂ addition skid with safety interlocks", "Acid-chloride scrubber (if needed)", "Flash chromatography", "Crystallizer"],
      batchPlan: {
        targetBatch: "100 g per lead analog (5 leads total = 500 g program)",
        startingMaterial: "Benzhydryl + thioglycolic acid + variable aryl/alkyl halides",
        keyVolumes: ["Ethanol: 15 L per 100 g", "EtOAc: 10 L", "Methanol: 5 L", "H₂O₂ (30 %): 1.2 L", "AcOH: 2 L"]
      },
      hazards: [
        "H₂O₂ oxidation exotherm — temperature-trip interlock required (ΔT > 10 °C)",
        "Lawesson's reagent — moisture-sensitive; releases H₂S on decomposition (malodor + toxic)",
        "Thioglycolic acid — strong malodor",
        "Acid chloride intermediates (if used) — scrubber for HCl"
      ],
      vendorNotes: [
        "Thioglycolic acid: Sigma",
        "Diphenylmethyl halides: Sigma, Combi-Blocks",
        "Lawesson's reagent: Sigma, TCI",
        "H₂O₂ (30 %): Solvay bulk"
      ],
      estCostPerKg: "$12,000 – $22,000 per lead analog"
    },
    safety: {
      acuteToxicity: "Modafinil-class — favorable; individual 11/12 analogs likely similar",
      chronicConcerns: ["H₂S exposure during Lawesson's reagent use"],
      contraindications: ["Compound-specific; modafinil class contraindications as baseline"],
      acuteSideEffects: ["Anti-inflammatory without strong CNS effects (hypothesized)"],
      ehsClassification: "GHS Cat. 4 oral; H₂S exposure controls for Lawesson's route"
    },
    publishedResearch: [
      { citation: "Jung JK, Kim JS, Kim SK et al. Simple Synthesis of Modafinil Derivatives and Their Anti-inflammatory Activity. Molecules. 2012;17(9):10446-10458.", url: "https://www.mdpi.com/1420-3049/17/9/10446" }
    ],
    patentInfo: "Jung 2012 published — academic composition space; formulation and method-of-use patents possible.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 18, evidenceQuality: 16, unmetMedicalNeed: 17, novelty: 13, universityInterest: 8 },
      harmBreakdown: { acuteToxicity: 3, chronicToxicity: 6, dependence: 2, schedulingRisk: 7 },
      netValue: 54,
      confidenceRationale: "MODERATE — published synthesis + cell-based efficacy data"
    },
    priorityTier: "Tier 2",
    nextSteps: [
      "Select 3–5 lead analogs from Jung 2012 based on potency + drug-likeness",
      "BV2 microglia assay replication (in-house or CRO)",
      "Partner with neuroinflammation lab (Parkinson's or Alzheimer's focus)",
      "File formulation/method-of-use patents"
    ]
  }
};